/**
 * Machine controls (ADR 0106 decisions 1 and 2): the one status a machine shows on the Machines
 * page and its drawer, whether new work is disabled on it, and the calls that disable, enable
 * and rename it. The drain lives in the hub's scheduler (state.ext.scheduler.nodes[id].drain) or,
 * on the control plane, in its machine-controls module (state.ext['machine-controls'].drains):
 * both answer the same routes, so the console reads whichever is there.
 */
import { MACHINE_CONTROLS_EXT, cloudVmStatus, machineLiveness } from '@orgus/protocol';
import type { CloudVm, Id, Job, Machine, MachineControlsState, MachineDrain, SchedulerState, Session } from '@orgus/protocol';
import { formatDuration } from '@orgus/ui';
import { api } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { SCHEDULER_EXT } from '../scheduler/model.ts';

export type MachineStateKey = 'online' | 'working' | 'stale' | 'disabled' | 'starting' | 'stopping' | 'deleting' | 'stopped' | 'offline';
export type MachineTone = 'success' | 'working' | 'attention' | 'neutral';

export interface MachineState {
  key: MachineStateKey;
  tone: MachineTone;
  label: string;
}

/**
 * The machine's one status on the Machines page and drawer (ADR 0106 decision 1). Offline and
 * not reporting come first (a disabled machine that is offline is offline), then Disabled, then
 * Working when a session or command runs, else Online. A VM Orgus created (ADR 0106 decision 3)
 * shows its own state first while it changes or is stopped: Starting, Stopping (after running
 * work, while Stop waits), Deleting, Stopped; a stopped VM is never "Offline".
 */
export function machineState(m: Machine, now: number, o: { working: number; drain: MachineDrain | null; vm?: Pick<CloudVm, 'state' | 'op' | 'machineId'> | null }): MachineState {
  const live = machineLiveness(m, now);
  const vm = o.vm ? cloudVmStatus(o.vm, live !== 'offline' && m.online) : null;
  const waiting = !!o.vm?.op?.waitUntil;
  if (vm === 'deleting') return { key: 'deleting', tone: 'working', label: waiting ? 'Deleting after running work' : 'Deleting' };
  if (vm === 'stopping') return { key: 'stopping', tone: 'working', label: waiting ? 'Stopping after running work' : 'Stopping' };
  if (vm === 'stopped') return { key: 'stopped', tone: 'neutral', label: 'Stopped' };
  if (vm === 'starting') return { key: 'starting', tone: 'working', label: 'Starting' };
  if (live === 'offline') return { key: 'offline', tone: 'neutral', label: `Offline for ${formatDuration(now - m.lastSeen)}` };
  if (live === 'stale') return { key: 'stale', tone: 'attention', label: `Not reporting for ${formatDuration(now - m.lastSeen)}` };
  if (o.drain) return { key: 'disabled', tone: 'neutral', label: 'Disabled' };
  if (o.working > 0) return { key: 'working', tone: 'working', label: 'Working' };
  return { key: 'online', tone: 'success', label: 'Online' };
}

/** Work running on a machine: sessions working or waiting on someone, and commands running. */
export function workingCount(sessions: readonly Pick<Session, 'status'>[], jobs: readonly Pick<Job, 'state'>[]): number {
  return sessions.filter((s) => s.status === 'working' || s.status === 'waiting').length + jobs.filter((j) => j.state === 'running').length;
}

/** Why a machine is disabled, in one sentence (the status's tooltip). */
export function disabledText(d: MachineDrain, now: number): string {
  return `Disabled by ${d.by} ${formatDuration(now - d.at)} ago${d.reason ? `: ${d.reason}` : ''}. Running work finishes; nothing new starts.`;
}

/** The drain of one machine from whichever server state carries it; undefined: no server can disable it. */
export function drainFrom(ext: Record<string, unknown>, machineId: Id): MachineDrain | null | undefined {
  const sched = ext[SCHEDULER_EXT] as SchedulerState | undefined;
  const controls = ext[MACHINE_CONTROLS_EXT] as MachineControlsState | undefined;
  if (!sched && !controls) return undefined;
  return sched?.nodes[machineId]?.drain ?? controls?.drains[machineId] ?? null;
}

/** The drain of one machine; undefined while no server state says whether it can be disabled. */
export function useMachineDrain(machineId: Id | undefined): MachineDrain | null | undefined {
  return useOrgus((s) => (machineId ? drainFrom(s.ext, machineId) : undefined));
}

/** Clean a display name as the servers do: printable, trimmed, at most 120 characters. */
export function cleanMachineName(raw: string): string {
  return raw.replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e\u2066-\u2069]/g, '').trim().slice(0, 120);
}

/** Set a machine's display name (PATCH /api/machines/:id, admin, on the hub and the control plane). */
export async function renameMachine(m: Machine, raw: string): Promise<void> {
  const name = cleanMachineName(raw);
  const st = useOrgus.getState();
  if (st.mode === 'mock') {
    st.applyPatch({ at: Date.now(), upserts: { machines: [{ ...m, name }] }, removes: {} });
    return;
  }
  await api(`/api/machines/${encodeURIComponent(m.id)}`, { method: 'PATCH', body: { name } });
}
